import React from 'react';
import { formatEther } from 'viem';
import type { MintSchedule } from '@soundxyz/sdk';
import type { Maybe } from '@soundxyz/utils';
import { useEthToUsdValue } from '@hooks/useEthToUsd';
import { useMintPrice } from '@store/sdk/reactQuery';
import { formatWeiToEthDisplayFree } from '@utils/helpers';
import { PriceDropdown } from '../shared';

export const PriceBreakdown = ({
  schedule,
  quantity,
  chainId,
  editionAddress,
}: {
  schedule: Maybe<MintSchedule>;
  quantity: number;
  chainId: number;
  editionAddress: string;
}) => {
  const { ethToUsd, isLoading: isUsdConversionLoading } = useEthToUsdValue();
  const ethToUsdValue = ethToUsd || 0;

  const basePrice = schedule?.price || 0n;
  const basePriceEthString = formatWeiToEthDisplayFree({
    weiValue: basePrice,
    maxDecimals: 6,
  });

  const { totalMintPriceWei } = useMintPrice({
    editionAddress,
    mintSchedule: schedule,
    chainId,
    quantity,
  });

  // This component should already be handing null state
  const totalFees = totalMintPriceWei?.fees ?? 0n;

  // we don't show at all if zero
  const totalFeesEthString = formatWeiToEthDisplayFree({
    weiValue: totalFees,
    maxDecimals: 6,
  });

  const totalBeforeFeesWei = totalMintPriceWei?.subTotal ?? 0n;
  const totalBeforeFeesEthString = formatWeiToEthDisplayFree({
    weiValue: totalBeforeFeesWei,
    maxDecimals: 6,
  });

  const totalPriceEthString = formatWeiToEthDisplayFree({
    weiValue: totalMintPriceWei?.total ?? 0n,
    maxDecimals: 6,
  });

  const totalPriceEth = formatEther(totalMintPriceWei?.total ?? 0n);
  const totalUsdPrice = (ethToUsdValue * Number(totalPriceEth)).toFixed(2);
  const isMintPriceLoading = totalMintPriceWei == null;

  return (
    <div className="flex w-full flex-col gap-2">
      <PriceDropdown
        chainId={chainId}
        totalPriceEthString={totalPriceEthString}
        totalUsdPrice={totalUsdPrice}
        basePriceEthString={basePriceEthString}
        quantity={quantity}
        totalBeforeFeesEthString={totalBeforeFeesEthString}
        totalFees={totalFees}
        totalFeesEthString={totalFeesEthString}
        totalPriceWei={totalMintPriceWei?.total ?? 0n}
        isLoading={isMintPriceLoading || isUsdConversionLoading}
        isGA={false}
      />
    </div>
  );
};
